import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ClipboardList, Download, HardDrive, Loader2, LogOut, BarChart3, Mail, Send } from "lucide-react";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminLock, adminFetch, useAdminToken } from "@/components/AdminAuth";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  submitted: { label: "Submitted", cls: "border-blueprint/30 bg-blueprint/5 text-blueprint" },
  paid: { label: "Paid — intake pending", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  awaiting_payment: { label: "Checkout started", cls: "border-border bg-ink/5 text-ink/50" },
  lead: { label: "Lead — details captured", cls: "border-ink/20 bg-paper text-ink/60" },
};

const money = (cents) =>
  cents == null ? "—" : `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const FollowUpDialog = ({ req, token, onClose, onSent }) => {
  const first = (req.facility?.contactName || "").trim().split(" ")[0] || "there";
  const toEmail = req.facility?.email || req.customer_email || "";
  const isPaidNudge = req.status === "paid";
  const [subject, setSubject] = useState(
    isPaidNudge
      ? `Finish your shielding design intake — ${req.facility?.name || "Heartland Physics"}`
      : `Your shielding design request — ${req.facility?.name || "Heartland Physics"}`
  );
  const [body, setBody] = useState(
    isPaidNudge
      ? `Hi ${first},\n\nThank you — your payment is in and your project is booked. The only thing left is the intake form (per-room details and floor plans), which takes about ten minutes.\n\nTap the button below to pick up right where you left off. Questions? Just reply to this email.`
      : `Hi ${first},\n\nI noticed you started a shielding design request${req.facility?.name ? ` for ${req.facility.name}` : ""} but didn't get a chance to finish. Happy to answer any questions about pricing, turnaround, or what your state will require — or help you pick it back up whenever you're ready.\n\nJust reply to this email and it comes straight to me.`
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    setSending(true);
    setErr("");
    try {
      const r = await adminFetch(token, `${API}/api/admin/requests/${req.id}/follow-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject,
          body,
          ...(isPaidNudge && req.resume_url ? { cta_url: req.resume_url, cta_label: "Continue your intake form" } : {}),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Send failed");
      setSent(true);
      onSent(req.id);
    } catch (e) {
      setErr(e.message || "Send failed");
    }
    setSending(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border bg-white sm:rounded-none" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-extrabold tracking-tight text-ink">Follow up with this lead</DialogTitle>
          <DialogDescription className="text-sm text-ink/55">
            Goes to {toEmail} from Heartland Physics — their reply lands in your inbox.
            {isPaidNudge && req.resume_url ? " Includes a button linking back to their intake form." : ""}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="border border-blueprint/30 bg-blueprint/5 p-4 text-sm text-ink" data-testid="followup-sent">
            Sent to {req.facility?.email}. You can send another anytime.
          </div>
        ) : (
          <>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Subject</p>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="followup-subject" className="mt-1.5 w-full border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-blueprint" />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Message</p>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="followup-body" className="mt-1.5 w-full border border-border bg-white px-3 py-2.5 text-sm leading-relaxed outline-none focus:border-blueprint" />
            </div>
            {err && <p className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="followup-error">{err}</p>}
          </>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} data-testid="followup-cancel" className="border border-border px-4 py-2.5 text-xs font-semibold text-ink/70 transition-colors hover:border-blueprint hover:text-blueprint">
            {sent ? "Close" : "Cancel"}
          </button>
          {!sent && (
            <button onClick={send} disabled={sending || !subject.trim() || body.trim().length < 10} data-testid="followup-send" className="flex items-center gap-2 bg-blueprint px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-ink disabled:opacity-60">
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {sending ? "Sending…" : "Send follow-up"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminRequests = () => {
  usePageMeta("Requests | Heartland Physics", "Owner view of shielding design requests.");
  const { token, save, signOut } = useAdminToken();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [followUpFor, setFollowUpFor] = useState(null);

  const markFollowedUp = (id) =>
    setData((d) => ({
      ...d,
      requests: d.requests.map((x) =>
        x.id === id
          ? { ...x, follow_up_count: (x.follow_up_count || 0) + 1, follow_up_last_sent_at: new Date().toISOString() }
          : x
      ),
    }));

  useEffect(() => {
    if (!token) return;
    adminFetch(token, `${API}/api/admin/requests`)
      .then(async (r) => {
        if (r.status === 401) {
          signOut();
          return;
        }
        setData(await r.json());
      })
      .catch(() => setData({ requests: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <AdminLock
        onUnlock={save}
        blurb="This page is private. Enter the owner password to view shielding design requests."
      />
    );
  }

  const requests = data?.requests || [];

  return (
    <section data-testid="admin-requests-page" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
        <Reveal y={12}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Internal — Site owner</p>
              <h1 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Shielding design requests</h1>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Link to="/admin/drive" data-testid="admin-nav-drive" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <HardDrive size={13} /> Drive
              </Link>
              <Link to="/admin/analytics" data-testid="admin-nav-analytics" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <BarChart3 size={13} /> Analytics
              </Link>
              <button onClick={signOut} data-testid="admin-logout-button" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <LogOut size={13} /> Lock
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-ink/60">
            Every checkout and intake, newest first. Floor plans download directly from here.
          </p>
        </Reveal>

        <div className="mt-10" data-testid="admin-requests-list">
          {data === null ? (
            <p className="flex items-center gap-2 text-sm text-ink/60"><Loader2 size={16} className="animate-spin" /> Loading requests…</p>
          ) : requests.length === 0 ? (
            <div className="border border-border bg-white p-10 text-center" data-testid="admin-requests-empty">
              <ClipboardList size={22} className="mx-auto text-ink/30" />
              <p className="mt-3 text-sm text-ink/55">No requests yet. New checkouts and submissions will appear here.</p>
            </div>
          ) : (
            requests.map((r, i) => {
              const st = r.payment_status === "refunded"
                ? { label: "Refunded", cls: "border-red-200 bg-red-50 text-red-600" }
                : STATUS[r.status] || STATUS.awaiting_payment;
              const open = expanded === r.id;
              const title = (r.facility && r.facility.name) || r.customer_email || "Unnamed checkout";
              return (
                <div key={r.id} className="mb-3 border border-border bg-white" data-testid={`request-row-${i}`}>
                  <button
                    onClick={() => setExpanded(open ? null : r.id)}
                    data-testid={`request-expand-${i}`}
                    className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4 text-left transition-colors hover:bg-paper"
                  >
                    <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${st.cls}`} data-testid={`request-status-${i}`}>{st.label}</span>
                    <span className="font-heading text-sm font-bold text-ink">{title}</span>
                    <span className="font-mono text-xs text-ink/45">{r.room_count} room{r.room_count === 1 ? "" : "s"}</span>
                    <span className="font-mono text-xs font-semibold text-ink/70">{money(r.amount)}</span>
                    <span className="ml-auto flex items-center gap-2 font-mono text-xs text-ink/40">
                      {fmtDate(r.created_at)}
                      <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  {open && (
                    <div className="grid gap-6 border-t border-border px-5 py-5 md:grid-cols-2" data-testid={`request-detail-${i}`}>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Contact</p>
                        <div className="mt-2 text-sm leading-relaxed text-ink/75">
                          {r.facility?.contactName && <p>{r.facility.contactName}</p>}
                          {r.facility?.email && (
                            <p><a className="font-medium text-blueprint hover:text-ink" href={`mailto:${r.facility.email}`}>{r.facility.email}</a></p>
                          )}
                          {r.facility?.phone && <p>{r.facility.phone}</p>}
                          {r.facility?.address && <p className="text-ink/55">{r.facility.address}</p>}
                          {!r.facility?.name && r.customer_email && <p>Checkout email: {r.customer_email}</p>}
                          {!r.facility?.name && !r.customer_email && <p className="text-ink/45">No details yet.</p>}
                          {(r.facility?.email || r.customer_email) && ["lead", "awaiting_payment", "paid"].includes(r.status) && (
                            <button
                              onClick={() => setFollowUpFor(r)}
                              data-testid={`request-followup-${i}`}
                              className="mt-3 flex w-fit items-center gap-2 border border-blueprint/30 bg-blueprint/5 px-4 py-2.5 text-xs font-semibold text-blueprint transition-colors hover:bg-blueprint hover:text-white"
                            >
                              <Mail size={13} />
                              {r.status === "paid"
                                ? (r.follow_up_count ? `Send intake reminder again (${r.follow_up_count} sent)` : "Send intake reminder")
                                : (r.follow_up_count ? `Follow up again (${r.follow_up_count} sent)` : "Send follow-up email")}
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Rooms</p>
                        <div className="mt-2 flex flex-col gap-1 text-sm text-ink/75">
                          {(r.rooms || []).map((room, j) => (
                            <p key={j}>
                              Room {j + 1}: {room.modality || "—"}
                              {room.roomId ? ` · ${room.roomId}` : ""}
                              {room.makeModel ? ` · ${room.makeModel}` : ""}
                            </p>
                          ))}
                          {(!r.rooms || r.rooms.length === 0) && <p className="text-ink/45">Intake form not submitted yet.</p>}
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Floor plans</p>
                        <div className="mt-2 flex flex-col gap-1.5" data-testid={`request-files-${i}`}>
                          {(r.files || []).map((f) => (
                            <a
                              key={f.id}
                              href={`${API}/api/requests/${r.id}/files/${f.id}`}
                              data-testid={`request-file-${f.id}`}
                              className="flex items-center gap-2 text-sm font-medium text-blueprint transition-colors hover:text-ink"
                            >
                              <Download size={13} /> {f.name}
                              <span className="font-mono text-xs text-ink/40">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                            </a>
                          ))}
                          {(!r.files || r.files.length === 0) && <p className="text-sm text-ink/45">None uploaded.</p>}
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Timeline</p>
                        <div className="mt-2 font-mono text-xs leading-relaxed text-ink/55">
                          <p>Started: {fmtDate(r.created_at)}</p>
                          <p>Paid: {fmtDate(r.paid_at)}</p>
                          <p>Submitted: {fmtDate(r.submitted_at)}</p>
                          {r.follow_up_last_sent_at && <p>Followed up: {fmtDate(r.follow_up_last_sent_at)}</p>}
                          {r.intake_reminded_at && <p>Auto-reminded: {fmtDate(r.intake_reminded_at)}</p>}
                          <p className="mt-2 break-all">ID: {r.id}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        {followUpFor && (
          <FollowUpDialog req={followUpFor} token={token} onClose={() => setFollowUpFor(null)} onSent={markFollowedUp} />
        )}
      </div>
    </section>
  );
};

export default AdminRequests;
